import { createContext, useContext, useEffect, useState } from "react";
import config from "../config";

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // restore and validate session on mount
  useEffect(() => {
    const validateSession = async () => {
      const storedUser = localStorage.getItem("user");
      const accessToken = localStorage.getItem("access_token");

      if (!storedUser || !accessToken) {
        setLoading(false);
        return;
      }

      try {
        // try to validate token by making a lightweight API call
        const response = await fetch(`${config.apiUrl}/user/basic`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (response.ok) {
          // token is valid, restore user
          setUser(JSON.parse(storedUser));
        } else if (response.status === 401 || response.status === 422) {
          // token expired, try refresh
          const refreshToken = localStorage.getItem("refresh_token");

          if (refreshToken) {
            const refreshRes = await fetch(`${config.apiUrl}/refresh`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${refreshToken}`,
                "Content-Type": "application/json",
              },
              credentials: "include",
            });

            if (refreshRes.ok) {
              const refreshData = await refreshRes.json();
              localStorage.setItem("access_token", refreshData.access_token);
              setUser(JSON.parse(storedUser));
            } else {
              // refresh failed, clear everything
              console.log("Session expired, redirecting to login");
              logout();
            }
          } else {
            // no refresh token, clear everything
            logout();
          }
        } else {
          // non-auth errors (e.g. 500) — restore user optimistically, may be transient
          setUser(JSON.parse(storedUser));
        }
      } catch (error) {
        console.error("Failed to validate session:", error);
        // network error - try to restore user anyway (might be offline)
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          logout();
        }
      }

      setLoading(false);
    };

    validateSession();
  }, []);

  const login = (newUser) => {
    const userData = { ...newUser };
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    // clear all authentication data
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("active_user_id");
    localStorage.removeItem("termsAccepted");
    localStorage.removeItem("username");
    localStorage.removeItem("dev_profile"); // clear dev mode data
    sessionStorage.removeItem("userRole"); // reset role to default (donor/green)
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export async function fetchWithToken(url, options = {}) {
  const accessToken = localStorage.getItem("access_token");

  // get csrf token from cookie for non-GET requests
  const getCsrfToken = () => {
    const match = document.cookie.match(/csrf_access_token=([^;]+)/);
    return match ? match[1] : "";
  };

  // add Authorization header with access token
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
      "X-CSRF-TOKEN": getCsrfToken(),
    },
    credentials: "include",
  });

  // 401 = expired, 422 = bad signature — both mean the token is unusable
  if (response.status === 401 || response.status === 422) {
    const refreshToken = localStorage.getItem("refresh_token");

    if (refreshToken) {
      try {
        // try to get new access token using refresh token
        const refreshRes = await fetch(`${config.apiUrl}/refresh`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${refreshToken}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (refreshRes.ok) {
          const refreshData = await refreshRes.json();

          // store new access token
          localStorage.setItem("access_token", refreshData.access_token);

          // retry original request with new access token
          return fetch(url, {
            ...options,
            headers: {
              ...options.headers,
              Authorization: `Bearer ${refreshData.access_token}`,
              "Content-Type": "application/json",
            },
            credentials: "include",
          });
        }
      } catch (error) {
        console.error("Token refresh failed:", error);
      }
    }

    // refresh failed - clear everything and redirect to login
    localStorage.clear();
    sessionStorage.removeItem("userRole"); // reset role to default (donor/green)
    window.location.href = "/login";
    throw new Error("Session expired. Please log in again.");
  }

  return response;
}

const jsonWithNulls = (obj) =>
  JSON.stringify(obj, (_k, v) => (v === undefined ? null : v));

export const postWithToken = (url, body = {}, options = {}) => {
  return fetchWithToken(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: jsonWithNulls(body),
    ...options,
  });
};

// profile fetching functions
export async function fetchUserProfile() {
  // check if dev profile exists (for dev mode without database)
  const devProfile = localStorage.getItem("dev_profile");
  if (devProfile) {
    try {
      const profile = JSON.parse(devProfile);
      // return with default policy for dev mode
      return {
        profile,
        policy: { cooldown_days: 60 }, // default for dev mode
      };
    } catch (error) {
      console.error("Error parsing dev profile:", error);
      localStorage.removeItem("dev_profile");
    }
  }

  // normal API call for production
  try {
    const response = await fetchWithToken(`${config.apiUrl}/user/profile`);

    if (response.ok) {
      const data = await response.json();
      return {
        profile: data.user_details,
        // extract policy from response, with fallback defaults
        policy: data.policy || { cooldown_days: 60 },
      };
    }

    console.error("Failed to fetch user profile:", response.status);
    return null;
  } catch (error) {
    console.error("Error fetching user profile:", error);
    return null;
  }
}

export async function fetchUserBasicInfo() {
  // check if dev profile exists (for dev mode without database)
  const devProfile = localStorage.getItem("dev_profile");
  if (devProfile) {
    try {
      const profile = JSON.parse(devProfile);
      // return basic info format
      return {
        id: profile.id,
        user_id: profile.user_id,
        company_name: profile.company_name,
        is_active: profile.is_active,
      };
    } catch (error) {
      console.error("Error parsing dev profile:", error);
      localStorage.removeItem("dev_profile");
    }
  }

  // normal API call for production
  try {
    const response = await fetchWithToken(`${config.apiUrl}/user/basic`);

    if (response.ok) {
      const data = await response.json();
      return data.user_info;
    }

    console.error("Failed to fetch user basic info:", response.status);
    return null;
  } catch (error) {
    console.error("Error fetching user basic info:", error);
    return null;
  }
}
